import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { useThemeStore } from '../../stores/themeStore';
import { toast } from '../../stores/toastStore';

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isLoading, clearError } = useAuthStore();
  const { theme } = useThemeStore();
  const isLight = theme === 'light';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    const result = await login(email, password);
    if (result.success) {
      toast.success('登录成功', '欢迎回来');
      const searchParams = new URLSearchParams(location.search);
      const redirectTo = searchParams.get('from') || '/';
      navigate(redirectTo.startsWith('/') ? redirectTo : '/');
    } else {
      toast.error('登录失败', result.error);
    }
  };

  return (
    <div className={`h-screen flex flex-col items-center justify-center px-6 relative ${
      isLight ? 'bg-white' : 'bg-[#060908]'
    }`}>
      {/* 极简 - 仅Logo + 表单 + 链接，大量留白 */}

      <div className="w-full max-w-[320px]">
        {/* Logo */}
        <div className="flex justify-center mb-16">
          <Link to="/" className="flex items-center gap-2.5 group">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-transform duration-300 group-hover:scale-110 ${
              isLight
                ? 'bg-gradient-to-br from-primary to-teal-500'
                : 'bg-gradient-to-br from-primary to-teal-400'
            }`}>
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <span className={`text-base font-bold tracking-tight ${isLight ? 'text-stone-900' : 'text-white'}`}>
              AI SaaS
            </span>
          </Link>
        </div>

        {/* 标题 - 极简 */}
        <h1 className={`text-xl font-bold tracking-tight text-center mb-10 ${
          isLight ? 'text-stone-900' : 'text-white'
        }`}>
          登录
        </h1>

        {/* 表单 - 无标签，placeholder 即说明 */}
        <form onSubmit={handleSubmit} className="space-y-3">
          <label htmlFor="login-email" className="sr-only">邮箱</label>
          <input
            id="login-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={`w-full px-0 py-3 text-sm border-0 border-b focus:outline-none focus:ring-0 focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 bg-transparent transition-colors duration-200 ${
              isLight
                ? 'border-stone-200 text-stone-900 placeholder-stone-400 focus:border-primary'
                : 'border-white/[0.1] text-stone-100 placeholder-stone-600 focus:border-primary/60'
            }`}
            placeholder="邮箱"
            required
            autoComplete="email"
          />

          <label htmlFor="login-password" className="sr-only">密码</label>
          <input
            id="login-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={`w-full px-0 py-3 text-sm border-0 border-b focus:outline-none focus:ring-0 focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 bg-transparent transition-colors duration-200 ${
              isLight
                ? 'border-stone-200 text-stone-900 placeholder-stone-400 focus:border-primary'
                : 'border-white/[0.1] text-stone-100 placeholder-stone-600 focus:border-primary/60'
            }`}
            placeholder="密码"
            required
            autoComplete="current-password"
          />

          <div className="pt-6">
            <button
              type="submit"
              disabled={isLoading}
              className={`w-full py-3 rounded-lg text-sm font-semibold text-white transition-all duration-300 disabled:opacity-40 disabled:cursor-not-allowed ${
                isLight
                  ? 'bg-primary hover:bg-primary/90'
                  : 'bg-primary/20 hover:bg-primary/30 border border-primary/30'
              }`}
            >
              {isLoading ? '登录中...' : '继续'}
            </button>
          </div>
        </form>

        {/* 底部链接 - 极简 */}
        <div className={`mt-10 text-center text-xs ${isLight ? 'text-stone-400' : 'text-stone-600'}`}>
          没有账户？{' '}
          <Link to="/register" className={`font-medium transition-colors ${
            isLight ? 'text-stone-900 hover:text-emerald-600' : 'text-stone-300 hover:text-emerald-400'
          }`}>
            注册
          </Link>
        </div>
      </div>

      {/* 底部条款 - 极小字 */}
      <div className={`absolute bottom-8 text-[10px] tracking-wider ${isLight ? 'text-stone-300' : 'text-stone-700'}`}>
        登录即表示同意服务条款和隐私政策
      </div>
    </div>
  );
};

export default LoginPage;
